'use client';

import {
  FormControlLabel,
  List,
  MenuItem,
  Radio,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { NativeStandardConfigVaultProtectionModeEnum } from '@repo/api-gw-sdk';
import { useState } from 'react';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import { OptionListItem } from '@/components/shared/optionListItem';

import type { VaultProtectionMode } from './BackupPolicyCreationFlow';

const NoneOptionItem = ({
  isChecked,
  isLocked,
  onSelect,
}: {
  isChecked: boolean;
  isLocked: boolean;
  onSelect: () => void;
}) => (
  <OptionListItem
    data-testid='vault-protection-none-card'
    isChecked={isChecked}
    onClick={() => {
      if (!isLocked) onSelect();
    }}
    sx={isLocked ? { cursor: 'default' } : undefined}
  >
    <Stack
      className='w-full'
      direction='row'
      alignItems='flex-start'
      justifyContent='space-between'
      gap='40px'
    >
      <FormControlLabel
        checked={isChecked}
        disabled={isLocked}
        onClick={(e) => {
          e.preventDefault();
          if (!isLocked) onSelect();
        }}
        control={<Radio />}
        label={
          <Stack gap='4px'>
            <Typography variant='subtitle1'>None</Typography>
            <Typography variant='body2'>
              Use the default backup vault without logical air gap or vault
              lock.
            </Typography>
          </Stack>
        }
        sx={{ alignItems: 'flex-start', marginLeft: '0' }}
      />
    </Stack>
  </OptionListItem>
);

const AirGappedOptionItem = ({
  isChecked,
  isLocked,
  onSelect,
}: {
  isChecked: boolean;
  isLocked: boolean;
  onSelect: () => void;
}) => (
  <OptionListItem
    data-testid='vault-protection-air-gapped-card'
    isChecked={isChecked}
    onClick={() => {
      if (!isLocked) onSelect();
    }}
    sx={isLocked ? { cursor: 'default' } : undefined}
  >
    <Stack
      className='w-full'
      direction='row'
      alignItems='flex-start'
      justifyContent='space-between'
      gap='40px'
    >
      <FormControlLabel
        checked={isChecked}
        disabled={isLocked}
        onClick={(e) => {
          e.preventDefault();
          if (!isLocked) onSelect();
        }}
        control={<Radio />}
        label={
          <Stack gap='4px'>
            <Typography variant='subtitle1'>
              Logically air-gapped vault
            </Typography>
            <Typography variant='body2'>
              Backups are stored in a separate logically air-gapped vault. Vault
              lock is automatically enabled.
            </Typography>
          </Stack>
        }
        sx={{ alignItems: 'flex-start', marginLeft: '0' }}
      />
    </Stack>
  </OptionListItem>
);

const VaultLockOptionItem = ({
  isChecked,
  isLocked,
  currentMode,
  onSelect,
  onLockModeSelect,
}: {
  isChecked: boolean;
  isLocked: boolean;
  currentMode: VaultProtectionMode;
  onSelect: () => void;
  onLockModeSelect: (value: string) => void;
}) => (
  <OptionListItem
    data-testid='vault-protection-vault-lock-card'
    isChecked={isChecked}
    onClick={() => {
      if (!isLocked && !isChecked) onSelect();
    }}
    sx={isLocked ? { cursor: 'default' } : undefined}
  >
    <Stack
      className='w-full'
      direction='row'
      alignItems='flex-start'
      justifyContent='space-between'
      gap='40px'
    >
      <FormControlLabel
        checked={isChecked}
        disabled={isLocked}
        onClick={(e) => {
          e.preventDefault();
          if (!isLocked && !isChecked) onSelect();
        }}
        control={<Radio />}
        label={
          <Stack gap='4px'>
            <Typography variant='subtitle1'>Vault lock</Typography>
            <Typography variant='body2'>
              Protect backups with a vault lock to prevent deletion.
            </Typography>
          </Stack>
        }
        sx={{ alignItems: 'flex-start', marginLeft: '0' }}
      />
      {isChecked && (
        <TextField
          select
          size='small'
          data-testid='vault-protection-lock-mode-select'
          value={
            currentMode ===
            NativeStandardConfigVaultProtectionModeEnum.VaultLockCompliance
              ? 'compliance'
              : 'governance'
          }
          disabled={isLocked}
          placeholder='Select'
          label=''
          slotProps={{
            inputLabel: { shrink: true },
            select: {
              renderValue: (value) => {
                if (value === 'governance') return 'Governance mode';
                if (value === 'compliance') return 'Compliance mode';
                return '';
              },
              onClick: (e) => {
                e.stopPropagation();
              },
              MenuProps: {
                PaperProps: {
                  sx: {
                    maxWidth: 'min(560px, calc(100vw - 48px))',
                  },
                },
              },
            },
          }}
          onChange={(e) => {
            onLockModeSelect(e.target.value);
          }}
          sx={{ width: '50%', minWidth: '240px', flexShrink: 0 }}
        >
          <MenuItem
            value='governance'
            data-testid='vault-lock-mode-governance'
            sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
          >
            <Stack gap='4px' sx={{ paddingTop: '8px', paddingBottom: '8px' }}>
              <Typography variant='subtitle2'>Governance mode</Typography>
              <Typography variant='body2' color='text.secondary'>
                The lock can be managed or deleted by users with specific IAM
                permissions.
              </Typography>
            </Stack>
          </MenuItem>
          <MenuItem
            value='compliance'
            data-testid='vault-lock-mode-compliance'
            sx={{ alignItems: 'flex-start', whiteSpace: 'normal' }}
          >
            <Stack gap='4px' sx={{ paddingTop: '8px', paddingBottom: '8px' }}>
              <Typography variant='subtitle2'>Compliance mode</Typography>
              <Typography variant='body2' color='text.secondary'>
                The lock cannot be managed or deleted by any user, even by the
                root user (account owner) or AWS.
              </Typography>
            </Stack>
          </MenuItem>
        </TextField>
      )}
    </Stack>
  </OptionListItem>
);

export const VaultProtectionOptions = (props: {
  vaultProtectionMode: VaultProtectionMode;
  onModeChange: (mode: VaultProtectionMode) => void;
  isLocked: boolean;
}) => {
  const { vaultProtectionMode, onModeChange, isLocked } = props;
  const currentMode =
    vaultProtectionMode ?? NativeStandardConfigVaultProtectionModeEnum.None;
  const [expanded, setExpanded] = useState(
    currentMode !== NativeStandardConfigVaultProtectionModeEnum.None
  );

  const isNone =
    currentMode === NativeStandardConfigVaultProtectionModeEnum.None;
  const isAirGapped =
    currentMode === NativeStandardConfigVaultProtectionModeEnum.AirGapped;
  const isVaultLock =
    currentMode ===
      NativeStandardConfigVaultProtectionModeEnum.VaultLockGovernance ||
    currentMode ===
      NativeStandardConfigVaultProtectionModeEnum.VaultLockCompliance;

  const setMode = (mode: VaultProtectionMode) => {
    if (isLocked) return;
    onModeChange(mode);
  };

  const handleLockModeSelect = (value: string) => {
    if (value === 'governance') {
      setMode(NativeStandardConfigVaultProtectionModeEnum.VaultLockGovernance);
    } else if (value === 'compliance') {
      setMode(NativeStandardConfigVaultProtectionModeEnum.VaultLockCompliance);
    }
  };

  return (
    <Stack gap='12px' className='max-w-[760px]'>
      <Accordion
        expanded={expanded}
        onChange={(_, isExpanded) => setExpanded(isExpanded)}
      >
        <AccordionSummary>
          <Typography variant='h4'>Vault protection</Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ paddingRight: '0' }}>
          <Stack gap='24px'>
            {isLocked && (
              <Typography variant='body2'>
                Vault protection settings cannot be changed after policy
                creation.
              </Typography>
            )}

            <List sx={{ padding: '0' }}>
              <NoneOptionItem
                isChecked={isNone}
                isLocked={isLocked}
                onSelect={() =>
                  setMode(NativeStandardConfigVaultProtectionModeEnum.None)
                }
              />
              <VaultLockOptionItem
                isChecked={isVaultLock}
                isLocked={isLocked}
                currentMode={currentMode}
                onSelect={() =>
                  setMode(
                    NativeStandardConfigVaultProtectionModeEnum.VaultLockGovernance
                  )
                }
                onLockModeSelect={handleLockModeSelect}
              />
              <AirGappedOptionItem
                isChecked={isAirGapped}
                isLocked={isLocked}
                onSelect={() =>
                  setMode(NativeStandardConfigVaultProtectionModeEnum.AirGapped)
                }
              />
            </List>
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
};
